import { numberFormatterLocal } from '../utils/formatters';
import { roundNumber } from '../utils/number';

const MAX_FRACTION_DECIMALS = 5;

export interface CurrencyFormatterOptions {
  /** Whether to avoid fractions in the output */
  avoidTrailingZero?: boolean;
  /** The currency code to use for formatting */
  currency: string;
  /** Hide currency flag */
  hideCurrency?: boolean;
}

interface IException {
  [key: string]: { original: string; replacement: string };
}

/**
 * Exception currencies for which we replace the currency symbol with a custom one.
 */
export const EXCEPTIONS: IException = {
  COP: { original: '$', replacement: 'COP' },
  VES: { original: 'VES', replacement: 'Bs' },
  TWD: { original: '$', replacement: 'NT$' },
  TTD: { original: '$', replacement: 'TT$' },
  BTC6: { original: 'BTC6', replacement: 'μBTC' },
  BTC3: { original: 'BTC3', replacement: 'mBTC' },
  XAF: { original: 'FCFA', replacement: 'XAF' },
  GHS: { original: 'GH₵', replacement: 'GH¢' },
  RSD: { original: 'RSD', replacement: 'Дин.' },
  UZS: { original: 'UZS', replacement: 'лв' },
  KRW2: { original: 'KRW2', replacement: '₩' },
  INR1: { original: 'INR1', replacement: '₹' },
  KES: { original: 'KES', replacement: 'Ksh' },
  IDR1: { original: 'IDR1', replacement: 'Rp' },
  IDR2: { original: 'IDR2', replacement: 'Rp' },
  PEN: { original: 'PEN', replacement: 'S/.' },
  USDT: { original: 'USDT', replacement: '$' },
  ZMW: { original: 'ZK', replacement: 'ZMW' },
  BGN: { original: 'BGN', replacement: 'лв' },
  ETH: { original: 'ETH', replacement: 'Ξ' },
  ETH4: { original: 'ETH4', replacement: 'Ξ' },
  ETH7: { original: 'ETH7', replacement: 'Ξ' },
  ETH9: { original: 'ETH9', replacement: 'Ξ' },
  VND2: { original: 'VND2', replacement: '₫' },
  LAK2: { original: 'LAK2', replacement: 'LAK' },
  XOF: { original: 'XOF', replacement: 'F CFA' }, // Somehow Intl shows XOF for currency in android but F CFA in other devices
};

interface IRuleException {
  [key: string]: Intl.NumberFormatOptions;
}

/**
 * Exception currencies for which some specific formatting rules need to be applied
 */
export const RULES_EXCEPTIONS: IRuleException = {
  RSD: { minimumFractionDigits: 2 }, // Somehow Intl counts there are no fractions for RSD, but there ARE by ISO
  PYG: { currencyDisplay: 'symbol' },
};

export class CurrencyFormatterClass {
  private static readonly onlyNumbersAndPunctuation: RegExp = /[^\d\u0660-\u066C\u06F0-\u06F9\p{P}   _]+/gu;
  private static readonly negativeSignRegex = /[-−]/g; // Though visually same but are different characters
  /**
   * Cache is used to store the Intl.NumberFormat objects so we don't have to create them every time we need to format a number.
   */
  private static cache: Record<string, Intl.NumberFormat> = {};
  private static customCurrencies: Record<string, boolean> = {};

  /**
   * Configure and cache formatter for currency.
   */
  private setCurrencyFormatting = (cacheTag: string, locale: string, options: CurrencyFormatterOptions): void => {
    const currencyOptions: Intl.NumberFormatOptions = {
      style: 'currency',
      currency: options.currency,
      currencyDisplay: options.hideCurrency ? 'code' : 'narrowSymbol',
      minimumFractionDigits: options.avoidTrailingZero ? 0 : undefined,
      ...RULES_EXCEPTIONS[options.currency],
    };

    CurrencyFormatterClass.cache[cacheTag] = new Intl.NumberFormat(numberFormatterLocal(locale), currencyOptions);
  };

  /**
   * Configure and cache formatter for decimals.
   * This formatter is used as a fall-back option for custom currencies not supported by the Intl API.
   */
  private setDecimalFormatting = (cacheTag: string, locale: string, options: CurrencyFormatterOptions): void => {
    const decimalOptions: Intl.NumberFormatOptions = {
      style: 'decimal',
      minimumFractionDigits: options.avoidTrailingZero ? 0 : 2,
      maximumFractionDigits: MAX_FRACTION_DECIMALS,
    };

    CurrencyFormatterClass.cache[cacheTag] = new Intl.NumberFormat(numberFormatterLocal(locale), decimalOptions);
  };

  /**
   * Set formatting configuration for a given currency, and return it as Intl.NumberFormat object from the cache.
   * Call `format()` or `formatToParts()` method on it, and pass a value which needs to be formated.
   */
  private getFormatter = (locale: string, options: CurrencyFormatterOptions): Intl.NumberFormat => {
    const cacheTag = [
      options.currency ? options.currency : 'decimal',
      locale,
      Boolean(options.hideCurrency),
      Boolean(options.avoidTrailingZero),
    ].join('_');

    // Write formatter to cache
    if (!CurrencyFormatterClass.cache[cacheTag]) {
      // Apply currency formatting, if currency hasn't been marked as custom
      if (!CurrencyFormatterClass.customCurrencies[options.currency]) {
        try {
          // Format currency using Intl API
          this.setCurrencyFormatting(cacheTag, locale, options);
        } catch (error) {
          // If currency is not supported by Intl API, e.g. DOGE, mark it as custom and format as decimal
          CurrencyFormatterClass.customCurrencies[options.currency] = true;
          this.setDecimalFormatting(cacheTag, locale, options);
        }
      } else {
        // Apply decimal formatting for custom currency by default
        this.setDecimalFormatting(cacheTag, locale, options);
      }
    }

    // Return formatter from cache
    return CurrencyFormatterClass.cache[cacheTag] as Intl.NumberFormat;
  };

  /**
   * Safeguard for negative zero and close-to-zero values.
   * Note: `signDisplay: "negative"` option is not supported in earlier browser versions, thus, handling manually.
   */
  private negativeZeroSave = (value: number, formatter: Intl.NumberFormat): number => {
    // Get the number of fraction digits for the given currency
    const { maximumFractionDigits } = formatter.resolvedOptions();
    const roundedValue = roundNumber(value, maximumFractionDigits ?? MAX_FRACTION_DECIMALS);

    // Treat -0 and close-to-zero values as 0
    if (Object.is(value, -0) || Math.abs(roundedValue) === 0) {
      return 0;
    }

    return value;
  };

  /**
   * Remove all non-numeric symbols, i.e. currency code, from the string.
   */
  private hideCurrency = (formattedString: string): string => {
    return formattedString.replace(CurrencyFormatterClass.onlyNumbersAndPunctuation, '').trim();
  };

  /**
   * Add the currency symbol before the string value.
   * Used for handling custom currencies, which were initially formatted as decimals and miss the currency symbol.
   */
  private prependCurrency = (formattedString: string, options: CurrencyFormatterOptions): string => {
    return `${options.currency} ${formattedString}`;
  };

  /**
   * Format the custom currency, i.e., handle negative numbers and adding symbol
   */
  private formatCustomCurrency = (
    value: number,
    formattedString: string,
    options: CurrencyFormatterOptions
  ): string => {
    const prefix = value < 0 ? '-' : '';
    const absoluteValue = formattedString.replace(CurrencyFormatterClass.negativeSignRegex, '');
    const valueWithCurrency = this.prependCurrency(absoluteValue, options);

    return `${prefix}${valueWithCurrency}`;
  };

  public replaceExceptionSymbol = (value: string, currency: string): string => {
    const exception = EXCEPTIONS[currency];

    if (exception) {
      return value.replace(exception.original, exception.replacement);
    }

    return value;
  };

  public format = (value: number, locale: string, options: CurrencyFormatterOptions): string => {
    const formatter = this.getFormatter(locale, options);
    const safeValue = this.negativeZeroSave(value, formatter);
    let formattedString: string = formatter.format(safeValue);

    // Final string processing
    if (options.hideCurrency) {
      formattedString = this.hideCurrency(formattedString);
    } else {
      // Prepend currency symbol for a custom currency, e.g. DOGE
      if (CurrencyFormatterClass.customCurrencies[options.currency]) {
        formattedString = this.formatCustomCurrency(value, formattedString, options);
      }

      // Replace currency symbol if exception for it exists
      formattedString = this.replaceExceptionSymbol(formattedString, options.currency);
    }

    return formattedString;
  };

  /**
   * Returns an array with the main (left side) and fraction (right side) parts separated by the decimal symbol.
   */
  public splitFraction(value: number, locale: string, options: CurrencyFormatterOptions): [string, string] {
    const exception = EXCEPTIONS[options.currency];
    const safeValue = this.negativeZeroSave(value, this.getFormatter(locale, options));

    /**
     * Override default formatter options to avoid trailing zero for integer values.
     * Logic is adjusted for WinMessageConnected component.
     */
    if (Number.isInteger(safeValue)) {
      options.avoidTrailingZero = true;
    }

    const parts: Intl.NumberFormatPart[] = this.getFormatter(locale, options).formatToParts(safeValue);

    let mainPart: string = '';
    let fractionPart: string = '';
    let isDecimalFound: boolean = false;

    /**
     * Iterate over type-value pairs, which suppose to compose the final string.
     * Instead of using Intl API format() method to generate a single string, we rely on formatToParts() and build the strings ourselves.
     *
     * Note: Currency code could contain separator symbols, e.g. `Дин.`, `S/.`.
     * Instead of splitting formatted string by `decimalSeparator` from the store, using this approach to avoid a premature split.
     *
     */
    for (const part of parts) {
      // Replace currency symbol if exception for it exists
      if (part.type === 'currency' && exception) {
        part.value = exception.replacement;
      }

      if (isDecimalFound) {
        // Once the decimal symbol is found, construct the fraction part
        fractionPart += part.value;
      } else if (part.type === 'decimal') {
        // Once the decimal symbol is found, add it to the fraction part
        isDecimalFound = true;
        fractionPart += part.value;
      } else {
        // Construct the main part
        mainPart += part.value;
      }
    }

    // Final string processing
    if (options.hideCurrency) {
      mainPart = this.hideCurrency(mainPart);
      fractionPart = this.hideCurrency(fractionPart);
    } else if (CurrencyFormatterClass.customCurrencies[options.currency]) {
      mainPart = this.formatCustomCurrency(value, mainPart, options);
      mainPart = this.replaceExceptionSymbol(mainPart, options.currency);
    }

    return [mainPart, fractionPart];
  }
}

export const CurrencyFormatter = new CurrencyFormatterClass();
